import React, { createContext, useState, useEffect, useRef } from 'react';
import { Session } from '@supabase/supabase-js';
import posthog from 'posthog-js';
import { Profile, UpdateProfileDTO } from '../types';
import { authService } from '../services/api/authService';
import { profileService } from '../services/api/profileService';
import { isSupabaseConfigured } from '../lib/supabase';
import { MOCK_USERS } from '../services/data/mockData';

const INVITE_TOKEN_KEY = 'sl_pending_invite_token';

// Upper bound on how long to wait for Supabase's background type=recovery token
// exchange to finish before giving up and showing the reset form anyway.
const RECOVERY_SESSION_TIMEOUT_MS = 8000;

function readAndClearInviteToken(): string | null {
  const params = new URLSearchParams(window.location.search);
  const token = params.get('invite');
  if (token) {
    sessionStorage.setItem(INVITE_TOKEN_KEY, token);
    params.delete('invite');
    const newUrl = `${window.location.pathname}${params.toString() ? `?${params}` : ''}`;
    window.history.replaceState({}, '', newUrl);
  }
  return sessionStorage.getItem(INVITE_TOKEN_KEY);
}

interface AuthContextType {
  session: Session | null;
  profile: Profile | null;
  isGuest: boolean;
  authState: 'BOOTING' | 'AUTHENTICATED' | 'UNAUTHENTICATED' | 'RECOVERING';
  setProfile: (profile: Profile | null) => void;
  updateProfile: (updates: UpdateProfileDTO) => Promise<void>;
  loginAsGuest: (role?: 'Athlete' | 'Coach' | 'AD') => void;
  signOut: () => Promise<void>;
  deleteAccount: () => Promise<{ error: Error | null }>;
}

export const AuthContext = createContext<AuthContextType | undefined>(undefined);

export const AuthProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const [authState, setAuthState] = useState<
    'BOOTING' | 'AUTHENTICATED' | 'UNAUTHENTICATED' | 'RECOVERING'
  >('BOOTING');
  const [session, setSession] = useState<Session | null>(null);
  const [profile, setProfile] = useState<Profile | null>(null);
  const [isGuest, setIsGuest] = useState(false);
  const isRecoveringRef = useRef(false);

  const resolveProfile = async (
    userId: string,
    email?: string,
    userMetadata?: Record<string, unknown>
  ): Promise<boolean> => {
    try {
      const data = await profileService.getById(userId);
      if (data) {
        // Check if the 14-day free trial has expired
        if (data.subscription_status === 'trial' && data.trial_started_at) {
          const trialEnd = new Date(
            new Date(data.trial_started_at).getTime() + 14 * 24 * 60 * 60 * 1000
          );
          if (new Date() > trialEnd) {
            await profileService.update(userId, { subscription_status: 'expired' });
            data.subscription_status = 'expired';
          }
        }

        // Back-fill birth_year from auth metadata on first login after signup
        if (data.birth_year == null && userMetadata?.birth_year) {
          const metaBirthYear = Number(userMetadata.birth_year);
          if (!isNaN(metaBirthYear)) {
            await profileService.update(userId, { birth_year: metaBirthYear });
            data.birth_year = metaBirthYear;
          }
        }

        // Save the founding promo source once, the first time it's seen — SubscriptionModal
        // still needs the localStorage flag to decide which price to charge, so it's left
        // in place here rather than cleared (it gets cleared when checkout actually starts).
        const pendingPromo = localStorage.getItem('scoutloop_promo');
        if (pendingPromo === 'founding' && data.promo_source !== 'founding') {
          profileService
            .update(userId, { promo_source: 'founding' })
            .catch(() => {/* best-effort — never block login */});
          data.promo_source = 'founding';
        }

        setProfile(data);

        // Accept a pending invite if one was stored from the signup URL
        const pendingToken = sessionStorage.getItem(INVITE_TOKEN_KEY);
        if (pendingToken) {
          sessionStorage.removeItem(INVITE_TOKEN_KEY);
          authService.acceptAthleteInvite(pendingToken, userId).then(({ accepted, reason }) => {
            if (!accepted && reason === 'expired') {
              // toast is not available here; AuthContext has no toast access.
              // The AuthScreen will have shown the "expired" message at render time.
            }
          }).catch(() => {/* best-effort — never block login */});
        }

        posthog.identify(userId, {
          email,
          role: data.role,
          sport: data.sport,
          grad_year: data.grad_year,
          school: data.school,
          subscription_status: data.subscription_status,
          subscription_plan: data.subscription_plan,
        });
        return true;
      }
      return false;
    } catch (err) {
      console.error('Profile resolution failed', err);
      return false;
    }
  };

  useEffect(() => {
    // Read ?invite=<token> from the URL and store in sessionStorage immediately.
    // This ensures the token survives email confirmation redirects.
    readAndClearInviteToken();

    // Detect recovery links before anything async runs — Supabase puts type=recovery
    // in the URL hash when the user clicks a password reset email link.
    const isRecovery = window.location.hash.includes('type=recovery');
    if (isRecovery) isRecoveringRef.current = true;

    // Guards against the timeout fallback and the PASSWORD_RECOVERY event both firing
    // — whichever resolves first wins, the other is a no-op.
    let recoverySessionResolved = false;
    let recoveryTimeoutId: number | undefined;

    const initAuth = async () => {
      if (!isSupabaseConfigured()) {
        setAuthState('UNAUTHENTICATED');
        return;
      }

      // Fast path: recovery link. Supabase exchanges the type=recovery hash for a real
      // session asynchronously in the background, so a getSession() call made the
      // instant the page loads frequently races ahead of that exchange and comes back
      // empty. Only commit to RECOVERING once a real session actually exists — either
      // from this immediate check (covers refresh, where the exchange already finished
      // and persisted) or from the PASSWORD_RECOVERY event fired by the listener below
      // once the exchange completes. Otherwise the "enter new password" form renders
      // with nothing behind it and submitting silently fails.
      if (isRecovery) {
        try {
          const response = await authService.getSession();
          if ('data' in response && response.data.session) {
            recoverySessionResolved = true;
            setSession(response.data.session);
            setAuthState('RECOVERING');
            return;
          }
        } catch {
          // fall through — still wait for the PASSWORD_RECOVERY event below
        }

        // No session yet. Stay on the loading screen (authState is still BOOTING) and
        // let the PASSWORD_RECOVERY handler in onAuthStateChange pick it up once the
        // exchange finishes. Time out so a genuinely invalid/expired link doesn't
        // strand the user on an infinite spinner — worst case, this reproduces today's
        // behavior (form shown without a confirmed session) instead of hanging forever.
        recoveryTimeoutId = window.setTimeout(() => {
          if (!recoverySessionResolved) {
            recoverySessionResolved = true;
            setAuthState('RECOVERING');
          }
        }, RECOVERY_SESSION_TIMEOUT_MS);
        return;
      }

      try {
        const response = await authService.getSession();
        if ('data' in response && response.data.session) {
          const currentSession = response.data.session;
          setSession(currentSession);
          await resolveProfile(currentSession.user.id, currentSession.user.email, currentSession.user.user_metadata);
          setIsGuest(false);
          setAuthState('AUTHENTICATED');
        } else {
          setAuthState('UNAUTHENTICATED');
        }
      } catch (err) {
        setAuthState('UNAUTHENTICATED');
      }
    };

    initAuth();

    const subscription = authService.onAuthStateChange(async (event, currentSession) => {
      if (event === 'PASSWORD_RECOVERY') {
        recoverySessionResolved = true;
        if (recoveryTimeoutId) window.clearTimeout(recoveryTimeoutId);
        isRecoveringRef.current = true;
        setSession(currentSession);
        setAuthState('RECOVERING');
      } else if (event === 'USER_UPDATED') {
        // Password was successfully updated — clear the recovery guard so the
        // subsequent SIGNED_IN event lands the user in the app normally.
        isRecoveringRef.current = false;
      } else if ((event === 'SIGNED_IN' || event === 'INITIAL_SESSION') && currentSession) {
        // Block SIGNED_IN from overwriting RECOVERING (Supabase fires both events
        // when the recovery token is exchanged on page load).
        if (isRecoveringRef.current) return;
        setSession(currentSession);
        await resolveProfile(currentSession.user.id, currentSession.user.email, currentSession.user.user_metadata);
        setIsGuest(false);
        setAuthState('AUTHENTICATED');
      } else if (event === 'SIGNED_OUT') {
        posthog.reset();
        setSession(null);
        setProfile(null);
        setIsGuest(false);
        setAuthState('UNAUTHENTICATED');
      }
    });

    return () => {
      if (subscription?.unsubscribe) subscription.unsubscribe();
      if (recoveryTimeoutId) window.clearTimeout(recoveryTimeoutId);
    };
  }, []);

  const loginAsGuest = (role: 'Athlete' | 'Coach' | 'AD' = 'Athlete') => {
    const mockProfile = role === 'Athlete' ? MOCK_USERS[0] : MOCK_USERS[2];
    setProfile({ ...mockProfile, role });
    setSession({
      user: { id: mockProfile.id, email: 'guest@scoutloop.com' },
      access_token: 'guest-token',
      refresh_token: 'guest-refresh',
      expires_in: 3600,
      token_type: 'bearer',
    } as Session);
    setIsGuest(true);
    setAuthState('AUTHENTICATED');
  };

  const updateProfile = async (updates: UpdateProfileDTO) => {
    if (!profile) return;

    if (isGuest) {
      setProfile({ ...profile, ...updates });
      return;
    }

    try {
      const updated = await profileService.update(profile.id, updates);
      setProfile(updated);
    } catch (err) {
      console.error('Update profile failed', err);
      throw err;
    }
  };

  const signOut = async () => {
    posthog.reset();
    if (session && !isGuest) {
      await authService.signOut();
    }
    setProfile(null);
    setSession(null);
    setIsGuest(false);
    setAuthState('UNAUTHENTICATED');
    window.location.replace(window.location.origin);
  };

  const deleteAccount = async (): Promise<{ error: Error | null }> => {
    const result = await authService.deleteAccount();
    if (!result.error) {
      posthog.capture('account_deleted');
      posthog.reset();
      setProfile(null);
      setSession(null);
      setIsGuest(false);
      setAuthState('UNAUTHENTICATED');
      window.location.replace(window.location.origin);
    }
    return result;
  };

  return (
    <AuthContext.Provider
      value={{
        session,
        profile,
        isGuest,
        authState,
        setProfile,
        updateProfile,
        loginAsGuest,
        signOut,
        deleteAccount,
      }}
    >
      {children}
    </AuthContext.Provider>
  );
};
